/**
 * WorkQueue — "My Work Today" seller home page.
 *
 * Replaces the kanban leads board. Three sections:
 *   1. New opportunities (system-curated)
 *   2. Active conversations (seller-owned)
 *   3. Needs attention (time-sensitive)
 *
 * Each lead card shows business name, "why now" signal, confidence, and one CTA.
 * Click opens preview panel. CTA navigates into deal flow.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Search, Plus, Sparkles, MessageSquare, AlertCircle, TrendingUp,
    ArrowUpDown, BriefcaseBusiness, Clock3, Send, MapPin, Inbox,
} from 'lucide-react';
import {Input} from '@/components/ui/input';
import {Button} from '@/components/ui/button';
import {Badge} from '@/components/ui/badge';
import {Card, CardContent} from '@/components/ui/card';
import {Progress} from '@/components/ui/progress';
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import {toast} from 'sonner';
import WorkQueueCard from '@/components/deal/WorkQueueCard';
import WorkQueueListItem from '@/components/deal/WorkQueueListItem';
import LeadPreviewPanel from '@/components/deal/LeadPreviewPanel';
import NearbyBusinessFinderDialog from '@/components/deal/NearbyBusinessFinderDialog';
import CSVImportDialog from '@/components/leads/CSVImportDialog';
import { computeWinRate } from '@/components/leads/mockData';
import {
    generateDealId,
} from '@/components/deal/mockDealData';
import useDealFlow from '@/hooks/useDealFlow';
import {Separator} from "@/components/ui/separator";
import { useOrganization } from '@/hooks/useOrganization';
import { workspacePriorityScore } from '@/services/sellerFlowMappers';

const FILTER_TABS = [
    {key: 'all', label: 'All'},
    {key: 'new', label: 'New'},
    {key: 'active', label: 'Active'},
    {key: 'stalled', label: 'Stalled'},
];

const SORT_OPTIONS = [
    {key: 'priority', label: 'Priority'},
    {key: 'newest', label: 'Newest'},
    {key: 'value', label: 'Value'},
];

const WEEKLY_GOAL = 15000; // mock target

const SECTIONS = [
    {
        key: 'needs_attention',
        title: 'Needs attention',
        icon: AlertCircle,
        emptyText: 'Nothing urgent right now',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
    },
    {
        key: 'new_opportunities',
        title: 'New opportunities',
        icon: Sparkles,
        emptyText: 'No new leads yet',
        color: 'text-coral',
        bgColor: 'bg-coral/5',
    },
    {
        key: 'active_conversations',
        title: 'Active conversations',
        icon: MessageSquare,
        emptyText: 'No active deals',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
    },
];

function getQueueSection(lead) {
    if (lead.needsAttention || lead.stale) return 'needs_attention';
    if (['awaiting_buyer', 'accepted_pending_billing', 'ready_to_launch', 'live'].includes(lead.queueStage)) {
        return 'active_conversations';
    }
    return 'new_opportunities';
}

function getStagePillMeta(leads = []) {
    const buckets = {
        discovery: 0,
        offer: 0,
        buyer: 0,
        live: 0,
    };

    leads.forEach((lead) => {
        if (lead.queueStage === 'live' || lead.queueStage === 'completed_or_terminal') {
            buckets.live += 1;
        } else if (['awaiting_buyer', 'accepted_pending_billing', 'ready_to_launch'].includes(lead.queueStage)) {
            buckets.buyer += 1;
        } else if (['creative_ready', 'offer_in_progress'].includes(lead.queueStage)) {
            buckets.offer += 1;
        } else {
            buckets.discovery += 1;
        }
    });

    return [
        { key: 'discovery', label: 'Discovery', count: buckets.discovery },
        { key: 'offer', label: 'Offer', count: buckets.offer },
        { key: 'buyer', label: 'Buyer', count: buckets.buyer },
        { key: 'live', label: 'Live', count: buckets.live },
    ];
}

function getStaleDaysFromLead(lead) {
    const reference = lead.lastActivityAt || lead.updatedAt || lead.triggerDate;
    if (!reference) return 0;
    return Math.max(0, Math.round((Date.now() - new Date(reference).getTime()) / (1000 * 60 * 60 * 24)));
}

export default function WorkQueue() {
    const {orgSlug} = useParams();
    const navigate = useNavigate();
    const deal = useDealFlow();
    const { createAdvertiser, currentOrg } = useOrganization();
    const resultsRef = useRef(null);

    const [search, setSearch] = useState('');
    const [selectedLead, setSelectedLead] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [csvOpen, setCsvOpen] = useState(false);
    const [nearbyFinderOpen, setNearbyFinderOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const [sortBy, setSortBy] = useState('priority');
    const [stageFilter, setStageFilter] = useState(null);
    const [queueDismissals, setQueueDismissals] = useState([]);

    useEffect(() => {
        deal.loadSellerHome().catch((error) => {
            console.error('Failed to load seller home', error);
            toast.error('Could not load seller workspace');
        });
    }, [deal.loadSellerHome]);

    const leads = useMemo(
        () => (deal.sellerHomeQueue || []).filter((lead) => !queueDismissals.includes(lead.id)),
        [deal.sellerHomeQueue, queueDismissals],
    );

    const summary = deal.sellerHomeSummary;
    const stageBreakdown = useMemo(() => getStagePillMeta(leads), [leads]);
    const leadIntelligence = useMemo(
        () => leads.map((leadItem) => ({
            lead: leadItem,
            score: {
                score: workspacePriorityScore(leadItem),
                priorityKey: leadItem.needsAttention ? 'high' : leadItem.stale ? 'medium' : 'low',
                priorityLabel: leadItem.needsAttention ? 'Act soon' : leadItem.stale ? 'Needs follow-up' : 'Monitor',
                priorityClassName: leadItem.needsAttention
                    ? 'border-coral/20 bg-coral/5 text-coral'
                    : leadItem.stale
                        ? 'border-amber-200 bg-amber-50 text-amber-700'
                        : 'border-slate-200 bg-slate-50 text-slate-600',
            },
            nextAction: {
                action: leadItem.recommendedNextStepCode,
                label: leadItem.recommendedNextStepText || 'Open workspace',
                description: leadItem.nextActionDescription || leadItem.recommendedNextStepText || 'Continue this workspace.',
            },
            stale: {
                isStale: leadItem.stale,
                days: getStaleDaysFromLead(leadItem),
            },
        })),
        [leads],
    );
    // F8 — Prefer the most-advanceable lead, not the hardest one. Stalled
    // / ghosted-buyer leads get pushed to the bottom so a fresh seller
    // doesn't open the day with their worst task.
    const topPriority = useMemo(
        () => (
            [...leadIntelligence]
                .filter(({lead: leadItem}) => leadItem.queueStage !== 'completed_or_terminal')
                .sort((a, b) => {
                    const aStalled = a.lead.stalled || a.lead.stale
                        || a.lead.queueStage === 'stalled' || a.lead.status === 'stalled' || a.lead.statusCode === 'stalled';
                    const bStalled = b.lead.stalled || b.lead.stale
                        || b.lead.queueStage === 'stalled' || b.lead.status === 'stalled' || b.lead.statusCode === 'stalled';
                    if (aStalled !== bStalled) return aStalled ? 1 : -1;
                    return b.score.score - a.score.score;
                })[0] || null
        ),
        [leadIntelligence],
    );
    const coachingMetrics = useMemo(() => ({
        highPriority: summary?.readyToLaunchCount || 0,
        readyToSend: summary?.awaitingBuyerCount || 0,
        followUps: summary?.awaitingBillingCount || 0,
        stale: summary?.needsAttentionCount || 0,
    }), [summary]);
    const weeklyProgress = useMemo(() => {
        const liveCount = summary?.liveCampaignsCount || 0;
        const target = Math.max(summary?.totalActiveWorkspaces || 1, 1);
        return Math.min(100, Math.round((liveCount / target) * 100));
    }, [summary]);

    // Stale lead counts per filter tab
    const staleCounts = useMemo(() => {
        const counts = {all: 0, new: 0, active: 0, stalled: 0};
        leads.forEach((lead) => {
            if (!lead.stale) return;
            counts.all++;
            if (['discovery', 'creative_ready', 'offer_in_progress'].includes(lead.queueStage)) counts.new++;
            if (['awaiting_buyer', 'accepted_pending_billing', 'ready_to_launch', 'live'].includes(lead.queueStage)) counts.active++;
            counts.stalled++;
        });
        return counts;
    }, [leads]);

    // Filter + sort + group leads into queue sections
    const filteredLeads = useMemo(() => {
        const query = search.toLowerCase();
        let filtered = leads.filter(
            (l) =>
                l.businessName.toLowerCase().includes(query) ||
                l.businessCategory.toLowerCase().includes(query) ||
                l.triggerEvent.toLowerCase().includes(query)
        );
        // Apply filter tab
        if (activeFilter === 'new') {
            filtered = filtered.filter((l) => ['discovery', 'creative_ready', 'offer_in_progress'].includes(l.queueStage));
        } else if (activeFilter === 'active') {
            filtered = filtered.filter((l) => ['awaiting_buyer', 'accepted_pending_billing', 'ready_to_launch', 'live'].includes(l.queueStage));
        } else if (activeFilter === 'stalled') {
            filtered = filtered.filter((l) => l.stale);
        }
        // Apply stage filter if set
        if (stageFilter !== null) {
            filtered = filtered.filter((lead) => {
                if (stageFilter === 'live') return ['live', 'completed_or_terminal'].includes(lead.queueStage);
                if (stageFilter === 'buyer') return ['awaiting_buyer', 'accepted_pending_billing', 'ready_to_launch'].includes(lead.queueStage);
                if (stageFilter === 'offer') return ['creative_ready', 'offer_in_progress'].includes(lead.queueStage);
                return lead.queueStage === 'discovery';
            });
        }
        // Apply sort — pre-compute expensive values into Maps to avoid recalculating per comparison
        if (sortBy === 'priority') {
            const scoreMap = new Map(filtered.map((l) => [l.id, workspacePriorityScore(l)]));
            filtered.sort((a, b) => (scoreMap.get(b.id) || 0) - (scoreMap.get(a.id) || 0));
        } else if (sortBy === 'newest') {
            filtered.sort((a, b) => new Date(b.lastActivityAt || b.updatedAt || b.triggerDate).getTime() - new Date(a.lastActivityAt || a.updatedAt || a.triggerDate).getTime());
        } else if (sortBy === 'value') {
            filtered.sort((a, b) => workspacePriorityScore(b) - workspacePriorityScore(a));
        }
        return filtered;
    }, [leads, search, activeFilter, sortBy, stageFilter]);

    const groupedLeads = useMemo(() => {
        const groups = {needs_attention: [], new_opportunities: [], active_conversations: []};
        // Hide the lead featured in the TOP PRIORITY card from the queue groups
        // below so the same business doesn't appear twice on one screen.
        const topPriorityLeadId = topPriority?.lead?.id;
        filteredLeads.forEach((lead) => {
            if (topPriorityLeadId && lead.id === topPriorityLeadId) return;
            const section = getQueueSection(lead);
            if (section && groups[section]) {
                groups[section].push(lead);
            }
        });
        return groups;
    }, [filteredLeads, topPriority?.lead?.id]);
    const visibleSectionCount = useMemo(
        () => SECTIONS.filter((section) => (groupedLeads[section.key] || []).length > 0).length,
        [groupedLeads],
    );
    const resultSummary = useMemo(
        () => SECTIONS.map((section) => ({
            key: section.key,
            title: section.title,
            count: groupedLeads[section.key]?.length || 0,
        })).filter((section) => section.count > 0),
        [groupedLeads],
    );
    const hasActiveControls = search.trim().length > 0 || activeFilter !== 'all' || stageFilter !== null;

    const winRate = useMemo(() => computeWinRate(leads), [leads]);
    const activeCount = summary?.totalActiveWorkspaces || leads.length;

    const scrollToResults = useCallback(() => {
        resultsRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, []);

    // ── Actions ──────────────────────────────────────────────────────────

    const handleSelect = useCallback((lead) => {
        setSelectedLead(lead);
        setPanelOpen(true);
    }, []);

    const startDeal = useCallback((lead) => {
        const dealId = generateDealId();
        deal.setLeadId(lead.id);
        deal.setLead(lead);
        deal.setDealId(dealId);
        deal.setReturnTo(`/org/${orgSlug}/leads`);

        if (lead.fromBackend && lead.advertiserId) {
            deal.upsertBusiness({
                id: lead.advertiserId,
                advertiserId: lead.advertiserId,
                workspaceId: lead.workspaceId,
                organizationId: currentOrg?.id || null,
                name: lead.businessName,
                address: lead.businessAddress,
                category: lead.businessCategory,
                rating: lead.businessRating,
                reviews: lead.businessReviewsCount,
            });
            deal.setActiveBusinessId(lead.advertiserId);
            navigate(`/org/${orgSlug}/deal/business/${lead.advertiserId}`);
        } else if (lead.enrichmentStatus === 'enriched') {
            const bizId = lead.businessProfileId || `bp-${Date.now()}`;
            deal.setBusinessProfile({
                id: bizId,
                place_id: null,
                website_url: lead.businessWebsite ? `https://${lead.businessWebsite}` : null,
                name: lead.businessName,
                address: lead.businessAddress,
                location_geo: null,
                category: lead.businessCategory,
                photos: lead.businessPhotos,
                logo_url: null,
                reviews: lead.businessReviewsCount,
                rating: lead.businessRating,
            });
            deal.addBusiness({
                id: bizId,
                name: lead.businessName,
                address: lead.businessAddress,
                category: lead.businessCategory,
                rating: lead.businessRating,
                reviews: lead.businessReviewsCount,
                website_url: lead.businessWebsite ? `https://${lead.businessWebsite}` : null,
                scope: null,
                createdAt: new Date().toISOString(),
            });
            deal.setActiveBusinessId(bizId);
            navigate(`/org/${orgSlug}/deal/business/${bizId}`);
        } else {
            navigate(`/org/${orgSlug}/deal/new`);
        }
        setPanelOpen(false);
    }, [currentOrg?.id, deal, orgSlug, navigate]);

    const handlePrimaryAction = useCallback((lead) => {
        startDeal(lead, '');
    }, [startDeal]);

    const handleAccept = useCallback((lead) => {
        startDeal(lead);
    }, [startDeal]);

    const handleSnooze = useCallback((lead) => {
        setQueueDismissals((prev) => [...prev, lead.id]);
        setPanelOpen(false);
        toast.info(`${lead.businessName} snoozed — will reappear later`);
    }, []);

    const handleDiscard = useCallback((lead, reason) => {
        setQueueDismissals((prev) => [...prev, lead.id]);
        setPanelOpen(false);
        toast.success(`${lead.businessName} discarded: ${reason}`);
    }, []);

    const handleAddBusiness = useCallback(() => {
        const dealId = generateDealId();
        deal.reset();
        deal.setDealId(dealId);
        deal.setReturnTo(`/org/${orgSlug}/leads`);
        navigate(`/org/${orgSlug}/deal/new`);
    }, [deal, orgSlug, navigate]);

    const handleAddNearbyBusiness = useCallback(async (place) => {
        const dealId = generateDealId();
        const category = place.types?.[0]
            ? place.types[0].replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
            : 'Local Business';

        if (!currentOrg?.id) {
            toast.error('Select an organization before adding a business.');
            return;
        }

        try {
            const advertiser = await createAdvertiser({
                name: place.name,
            });
            deal.setDealId(dealId);
            deal.setReturnTo(`/org/${orgSlug}/leads`);
            await deal.ensureWorkspaceForAdvertiser(advertiser.id, {
                place_id: place.placeId || null,
                name: place.name,
                address: place.address || '',
                website_url: place.website || null,
                category,
                rating: place.rating || 0,
                reviews: place.userRatingsTotal || 0,
                location_geo: place.location || null,
            }, { setActive: true, lightweight: true });
            setNearbyFinderOpen(false);
            toast.success(`${place.name} added to your workspace`);
            navigate(`/org/${orgSlug}/deal/business/${advertiser.id}`);
        } catch (error) {
            console.error('Failed to add nearby business', error);
            toast.error('Could not add that business');
        }
    }, [createAdvertiser, currentOrg?.id, deal, navigate, orgSlug]);

    const handleWorkNext = useCallback(() => {
        // Find first unworked lead (new opportunities first, then needs attention)
        const nextLead = groupedLeads.needs_attention[0] || groupedLeads.new_opportunities[0];
        if (nextLead) {
            handleSelect(nextLead);
        } else {
            toast.info('No leads to work — add a business to start');
        }
    }, [groupedLeads, handleSelect]);

    const handleFilterChange = useCallback((nextFilter) => {
        setActiveFilter(nextFilter);
    }, []);

    const handleSortChange = useCallback((nextSort) => {
        setSortBy(nextSort);
    }, []);

    const handleStageFilterChange = useCallback((nextStage) => {
        setStageFilter(nextStage);
    }, []);

    const handleSearchChange = useCallback((event) => {
        setSearch(event.target.value);
    }, []);

    const handleSearchKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            scrollToResults();
        }
    }, [scrollToResults]);

    const clearQueueControls = useCallback(() => {
        setSearch('');
        setActiveFilter('all');
        setSortBy('priority');
        setStageFilter(null);
    }, []);

    return (
        <div className="mx-auto max-w-6xl overflow-x-hidden px-4 pt-4 pb-12 sm:px-6">
            <Card
                className="overflow-hidden border-border bg-card shadow-sm">
                <CardContent className="p-0">
                    <div className="grid gap-5 px-4 py-5 sm:gap-6 sm:px-6 sm:py-6 lg:grid-cols-[1.5fr_1fr]">
                        <div className="order-1 min-w-0 space-y-4">
                            <div className="space-y-2">
                                <Badge variant="outline"
                                       className="rounded-full border-slate-300 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-600">
                                    Seller Workspace
                                </Badge>
                                <div className="space-y-1.5">
                                    <h1 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">My Work</h1>
                                </div>
                            </div>

                            {/* F2/F4 — Single primary CTA. "Work next best deal"
                                is the most useful affordance when there's
                                queued work; otherwise "Start a new deal" is
                                the primary path. "Find businesses around me"
                                is demoted to a secondary outline. Avoids the
                                three-equal-CTA cognitive load on first arrival. */}
                            <div className="flex flex-wrap items-center gap-2">
                                {(activeCount > 0) ? (
                                    <Button size="sm" onClick={handleWorkNext} className="bg-coral text-white hover:bg-coral/90 shadow-sm">
                                        <Sparkles className="h-4 w-4"/>
                                        Work next best deal
                                    </Button>
                                ) : (
                                    <Button size="sm" onClick={handleAddBusiness}
                                            className="bg-coral hover:bg-coral/90 text-white shadow-sm">
                                        <Plus className="h-4 w-4"/>
                                        Start a new deal
                                    </Button>
                                )}
                                <Button variant="outline" size="sm" onClick={handleAddBusiness}
                                        className="bg-white/80">
                                    <Plus className="h-4 w-4"/>
                                    {activeCount > 0 ? 'Add a business' : 'Or pick from a list'}
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setNearbyFinderOpen(true)}
                                        className="bg-white/80">
                                    <MapPin className="h-4 w-4"/>
                                    Find around me
                                </Button>
                            </div>

                            <div className="grid grid-cols-2 gap-x-6 gap-y-5 xl:grid-cols-4">
                                <div>
                                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                                        <BriefcaseBusiness className="h-3.5 w-3.5 text-emerald-600"/>
                                        Workspaces
                                    </div>
                                    <p className="mt-1 text-xl font-semibold text-slate-950 sm:text-2xl">{summary?.totalActiveWorkspaces || 0}</p>
                                    <p className="mt-0.5 text-xs text-slate-500">
                                        Active seller workspaces
                                    </p>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                                        <TrendingUp className="h-3.5 w-3.5 text-teal"/>
                                        Live
                                    </div>
                                    <p className="mt-1 text-xl font-semibold text-slate-950 sm:text-2xl">{summary?.liveCampaignsCount || 0}</p>
                                    <p className="mt-0.5 text-xs text-slate-500">Campaigns currently in delivery</p>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                                        <Send className="h-3.5 w-3.5 text-blue-600"/>
                                        Buyer follow-up
                                    </div>
                                    <p className="mt-1 text-xl font-semibold text-slate-950 sm:text-2xl">{summary?.awaitingBuyerCount || 0}</p>
                                    <p className="mt-0.5 text-xs text-slate-500">
                                        {summary?.awaitingBillingCount || 0} awaiting billing
                                    </p>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                                        <Clock3 className="h-3.5 w-3.5 text-amber-600"/>
                                        Attention
                                    </div>
                                    <p className="mt-1 text-xl font-semibold text-slate-950 sm:text-2xl">{coachingMetrics.stale}</p>
                                    <p className="mt-0.5 text-xs text-slate-500">
                                        {coachingMetrics.highPriority} high-priority to work now
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="order-2 min-w-0 rounded-2xl bg-slate-950 text-white shadow-lg shadow-slate-950/20 lg:row-span-2">
                            <div className="p-6 lg:p-7">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/60">Top
                                            Priority</p>
                                        {topPriority ? (
                                            <>
                                                <h2 className="mt-2 break-words text-2xl font-bold leading-tight">{topPriority.lead.businessName}</h2>
                                                <p className="mt-1 break-words text-sm text-white/70">{topPriority.lead.businessCategory}</p>
                                            </>
                                        ) : (
                                            <h2 className="mt-2 text-xl font-semibold leading-tight">No active
                                                deals</h2>
                                        )}
                                    </div>
                                    <Badge className="shrink-0 border-0 bg-white/10 text-white">
                                        {topPriority?.score.priorityLabel || 'Queue clear'}
                                    </Badge>
                                </div>

                                {topPriority ? (
                                    <>
                                        <div
                                            className="mt-5 space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                                            <div>
                                                <p className="text-xs font-medium uppercase tracking-[0.16em] text-white/50">Why
                                                    now</p>
                                                <p className="mt-1 text-sm text-white">{topPriority.lead.triggerEvent}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium uppercase tracking-[0.16em] text-white/50">Recommended
                                                    next step</p>
                                                <p className="mt-1 text-sm font-medium text-white">{topPriority.nextAction.label}</p>
                                                <p className="mt-1 text-sm text-white/70">{topPriority.nextAction.description}</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="rounded-xl bg-white/5 p-3">
                                                    <p className="text-xs text-white/50">Current stage</p>
                                                    <p className="mt-1 text-base font-semibold text-white">{topPriority.lead.queueStatusLabel || 'In progress'}</p>
                                                </div>
                                                <div className="rounded-xl bg-white/5 p-3">
                                                    <p className="text-xs text-white/50">Queue health</p>
                                                    <p className="mt-1 text-base font-semibold text-white">
                                                        {topPriority.stale.isStale ? `Stale ${topPriority.stale.days}d` : 'Fresh activity'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <Button
                                            onClick={() => handleSelect(topPriority.lead)}
                                            className="mt-5 w-full bg-white text-slate-950 hover:bg-white/90"
                                        >
                                            Open priority workspace
                                        </Button>
                                    </>
                                ) : (
                                    <div
                                        className="mt-5 rounded-2xl border border-dashed border-white/15 bg-white/5 p-4 text-sm text-white/70">
                                        Add a business to start building pipeline.
                                    </div>
                                )}

                                <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4">
                                    <div className="mb-2 flex items-center justify-between text-xs text-white/60">
                                        <span>Weekly goal progress</span>
                                        <span>{weeklyProgress}% of active workspaces live</span>
                                    </div>
                                    <Progress
                                        value={weeklyProgress}
                                        className="h-2 bg-white/10 [&>[data-slot=progress-indicator]]:bg-teal"
                                    />
                                    <p className="mt-3 text-xs text-white/60">
                                        {winRate > 0
                                            ? <>Win rate {winRate}% across {activeCount} open {activeCount !== 1 ? 'opportunities' : 'opportunity'}.</>
                                            : <>Goal: first close this week. {activeCount} {activeCount !== 1 ? 'opportunities' : 'opportunity'} in flight.</>}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="order-3 min-w-0 rounded-2xl border border-slate-200 bg-white/75 p-4 shadow-sm lg:col-start-1">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <BriefcaseBusiness className="h-4 w-4 text-slate-500"/>
                                        <h2 className="text-lg font-semibold text-slate-950">Seller queue</h2>
                                    </div>
                                    <p className="text-sm text-slate-600">
                                        {filteredLeads.length} business{filteredLeads.length !== 1 ? 'es' : ''} in queue
                                    </p>
                                </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                {FILTER_TABS.map((tab) => (
                                    <button
                                        key={tab.key}
                                        onClick={() => handleFilterChange(tab.key)}
                                        className={`rounded-full px-3.5 py-2 text-xs font-medium transition-colors ${
                                            activeFilter === tab.key
                                                ? 'bg-slate-950 text-white'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {tab.label}
                                        {staleCounts[tab.key] > 0 ? ` · ${staleCounts[tab.key]} stale` : ''}
                                    </button>
                                ))}
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                <div className="flex items-center gap-1.5 rounded-full bg-slate-100 p-1">
                                    <ArrowUpDown className="ml-2 h-3.5 w-3.5 text-slate-500"/>
                                    {SORT_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.key}
                                            onClick={() => handleSortChange(opt.key)}
                                            className={`rounded-full px-3.5 py-2 text-xs font-medium transition-colors ${
                                                sortBy === opt.key
                                                    ? 'bg-white text-slate-950 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-4 flex flex-col gap-3 lg:flex-row">
                                <div className="relative flex-1">
                                    <Search
                                        className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                                    <Input
                                        value={search}
                                        onChange={handleSearchChange}
                                        onKeyDown={handleSearchKeyDown}
                                        placeholder="Search by business, category, or trigger"
                                        className="h-11 rounded-xl border-slate-200 bg-slate-50 pl-9"
                                        aria-label="Search work queue"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" onClick={handleWorkNext} className="h-11 rounded-xl bg-white">
                                        <Sparkles className="h-4 w-4"/>
                                        Work next
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                                            Now showing
                                        </p>
                                        <p className="mt-1 text-sm text-slate-700">
                                            {filteredLeads.length} business{filteredLeads.length !== 1 ? 'es' : ''} across {visibleSectionCount} queue group{visibleSectionCount !== 1 ? 's' : ''}.
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {hasActiveControls ? (
                                            <Button variant="ghost" size="sm" onClick={clearQueueControls}
                                                    className="h-8 rounded-full px-3 text-slate-600">
                                                Reset filters
                                            </Button>
                                        ) : null}
                                        <Button variant="outline" size="sm" onClick={scrollToResults}
                                                className="h-8 rounded-full bg-white">
                                            Jump to results
                                        </Button>
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {resultSummary.length > 0 ? resultSummary.map((section) => (
                                        <Badge key={section.key} variant="outline"
                                               className="rounded-full border-slate-200 bg-white text-slate-700">
                                            {section.title} · {section.count}
                                        </Badge>
                                    )) : (
                                        <Badge variant="outline"
                                               className="rounded-full border-slate-200 bg-white text-slate-500">
                                            No matching businesses
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6 flex flex-wrap items-center gap-2">
                                <Separator/>
                            </div>

                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Deal funnel</span>
                                {stageBreakdown.map((s, i) => {
                                    const isActive = stageFilter === s.key;
                                    return (
                                        <button
                                            key={s.key}
                                            onClick={() => handleStageFilterChange(isActive ? null : s.key)}
                                            className={`rounded-full px-3.5 py-2 text-xs font-medium transition-colors ${
                                                isActive ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'
                                            }`}
                                        >
                                            {s.label} · {s.count}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div ref={resultsRef} className="scroll-mt-24"/>

            <div className="mt-6 hidden space-y-8 md:block">
                {SECTIONS.map((section) => {
                    const sectionLeads = groupedLeads[section.key] || [];
                    const SectionIcon = section.icon;

                    return (
                        <div key={section.key}>
                            {/* Section header */}
                            <div className="mb-3 flex items-center gap-2">
                                <SectionIcon className={`h-4 w-4 ${section.color}`}/>
                                <h2 className="text-sm font-semibold text-foreground">{section.title}</h2>
                                <Badge variant="outline" className="text-xs">{sectionLeads.length}</Badge>
                            </div>

                            {/* Cards or empty state */}
                            {sectionLeads.length > 0 ? (
                                <div className="grid gap-3 lg:grid-cols-2">
                                    {sectionLeads.map((lead, i) => (
                                        <WorkQueueCard
                                            key={lead.id}
                                            lead={lead}
                                            onSelect={handleSelect}
                                            onPrimaryAction={handlePrimaryAction}
                                            businesses={deal.businesses}
                                            creatives={deal.creatives}
                                            proposals={deal.proposals}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div
                                    className={`${section.bgColor} rounded-2xl border border-border/30 px-4 py-8 text-center`}>
                                    <Inbox className="h-5 w-5 text-muted-foreground mx-auto mb-2"/>
                                    <p className="text-sm text-muted-foreground">{section.emptyText}</p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-8 md:hidden">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-base font-semibold text-slate-950">Queue groups</h2>
                        <p className="mt-1 text-sm text-slate-600">Tap a section to scan businesses, then open a lead
                            for detail.</p>
                    </div>
                    <Badge variant="outline" className="rounded-full bg-slate-50 text-xs text-slate-600">
                        {filteredLeads.length} total
                    </Badge>
                </div>

                <Accordion
                    type="multiple"
                    defaultValue={['needs_attention', 'new_opportunities']}
                    className="rounded-3xl border border-slate-200 bg-white px-4 shadow-sm"
                >
                    {SECTIONS.map((section) => {
                        const sectionLeads = groupedLeads[section.key] || [];
                        const SectionIcon = section.icon;
                        const firstLead = sectionLeads[0];

                        return (
                            <AccordionItem key={section.key} value={section.key} className="border-slate-200">
                                <AccordionTrigger className="py-4 hover:no-underline">
                                    <div className="flex min-w-0 flex-1 items-start gap-3">
                                        <div
                                            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${section.bgColor}`}>
                                            <SectionIcon className={`h-4 w-4 ${section.color}`}/>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-slate-950">{section.title}</p>
                                                <Badge variant="outline" className="rounded-full text-[11px]">
                                                    {sectionLeads.length}
                                                </Badge>
                                            </div>
                                            <p className="mt-1 truncate text-sm text-slate-500">
                                                {firstLead ? firstLead.businessName : section.emptyText}
                                            </p>
                                        </div>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="pb-4">
                                    {sectionLeads.length > 0 ? (
                                        <div className="space-y-3">
                                            {sectionLeads.map((lead) => (
                                                <WorkQueueListItem
                                                    key={lead.id}
                                                    lead={lead}
                                                    onSelect={handleSelect}
                                                    businesses={deal.businesses}
                                                    creatives={deal.creatives}
                                                    proposals={deal.proposals}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div
                                            className={`${section.bgColor} rounded-2xl border border-border/30 px-4 py-6 text-center`}>
                                            <Inbox className="mx-auto mb-2 h-5 w-5 text-muted-foreground"/>
                                            <p className="text-sm text-muted-foreground">{section.emptyText}</p>
                                        </div>
                                    )}
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>

            {/* Preview panel */}
            <LeadPreviewPanel
                lead={selectedLead}
                open={panelOpen}
                onClose={() => setPanelOpen(false)}
                onAccept={handleAccept}
                onSnooze={handleSnooze}
                onDiscard={handleDiscard}
                businesses={deal.businesses}
                creatives={deal.creatives}
                proposals={deal.proposals}
                onResume={(lead) => {
                    const bizId = lead.businessProfileId;
                    if (bizId && deal.businesses?.[bizId]) {
                        navigate(`/org/${orgSlug}/deal/business/${bizId}`);
                        setPanelOpen(false);
                    }
                }}
            />

            {/* CSV Import */}
            <CSVImportDialog
                open={csvOpen}
                onClose={() => setCsvOpen(false)}
            />

            <NearbyBusinessFinderDialog
                open={nearbyFinderOpen}
                onOpenChange={setNearbyFinderOpen}
                onAddBusiness={handleAddNearbyBusiness}
            />
        </div>
    );
}
